/**
 * Inbox API client — App Inbox endpoints (PEL-639)
 *
 * Paths are relative to the mobile base URL (e.g. /api/v1/mobile).
 */
import type { ConsumerApiClient } from '../client';

// ─── Types ─────────────────────────────────────────────────────

export type InboxCategory = 'order' | 'promotion' | 'system' | 'engagement' | 'general';

export interface InboxMessage {
  id: string;
  title: string;
  body: string;
  category: InboxCategory;
  triggerKey: string;
  referenceId: string | null;
  referenceType: string | null;
  deepLink: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface InboxListResponse {
  messages: InboxMessage[];
  total: number;
  unreadCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface InboxUnreadCountResponse {
  count: number;
}

export interface GetInboxMessagesParams {
  page?: number;
  limit?: number;
  category?: InboxCategory;
}

// ─── API ───────────────────────────────────────────────────────

export interface InboxApi {
  /** Fetch paginated inbox messages. GET /inbox?page=1&limit=20&category=order */
  getMessages(params?: GetInboxMessagesParams): Promise<InboxListResponse>;
  /** Get just the unread count for badge display. GET /inbox/unread-count */
  getUnreadCount(): Promise<InboxUnreadCountResponse>;
  /** Mark a single message as read. PATCH /inbox/:id/read */
  markRead(messageId: string): Promise<void>;
  /** Mark all messages as read. POST /inbox/read-all */
  markAllRead(): Promise<void>;
  /** Delete a single message. DELETE /inbox/:id */
  deleteMessage(messageId: string): Promise<void>;
}

export function createInboxApi(client: ConsumerApiClient): InboxApi {
  return {
    getMessages(params) {
      const query = new URLSearchParams();
      if (params?.page) query.set('page', params.page.toString());
      if (params?.limit) query.set('limit', params.limit.toString());
      if (params?.category) query.set('category', params.category);
      const qs = query.toString();
      return client.get(`/inbox${qs ? `?${qs}` : ''}`);
    },

    getUnreadCount() {
      return client.get('/inbox/unread-count');
    },

    markRead(messageId) {
      return client.patch(`/inbox/${messageId}/read`);
    },

    markAllRead() {
      return client.post('/inbox/read-all');
    },

    deleteMessage(messageId) {
      return client.delete(`/inbox/${messageId}`);
    },
  };
}
